import { FIELD_MODES } from '../components/fields/FieldTools';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { graphql } from 'relay-runtime';
import '../../../stylesheets/components/globals.module.scss';
import '../../../stylesheets/components/theme.module.scss';
import '../../../stylesheets/components/buttonGlobals.module.scss';
import '../../../stylesheets/components/dropdownMenus.module.scss';
import '../../../stylesheets/components/dialog.module.scss';
import { BackIcon } from '../components/shared_icons/AppIcons';
import Text from '../tenant_portal/fields/Text';
import PortalButton from '../components/PortalButton';
import { useMutation } from 'react-relay';
import { ForgotPasswordLoginMutation$data } from './__generated__/ForgotPasswordLoginMutation.graphql';
import { ErrorType } from '../components/Types';
import PageHeader from '../tenant_portal/shared/PageHeader';
import SubmitSpinner from '../components/SubmitSpinner';
import SuccessPage from '../tenant_portal/shared/SuccessPage';
import { filterErrorsByPath, formatErrorMessages } from '../components/Tools';
import { useNavigate } from 'react-router-dom';
import loginStyles from '../../../stylesheets/components/login.module.scss';
import SomethingWentWrongAlert from './SomethingWentWrongAlert';

export const LoginForgotPasswordMutation = graphql`
  mutation ForgotPasswordLoginMutation($input: LoginForgotPasswordInput!) {
    loginForgotPassword(input: $input) {
      errors {
        path
        messages
      }
    }
  }
`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [backButtonPortalTarget, setBackButtonPortalTarget] =
    useState<HTMLElement | null>(null);
  const [emailField, setEmailField] = useState('');
  const [errors, setErrors] = useState<ErrorType[]>([]);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [commitMutation, isMutationInFlight] = useMutation(
    LoginForgotPasswordMutation
  );

  const baseErrors = filterErrorsByPath(errors);
  const formattedBaseErrorMessages = formatErrorMessages(baseErrors, '').trim();
  const somethingWentWrongAlert = (
    <SomethingWentWrongAlert
      body={formattedBaseErrorMessages}
      resetKey={errors}
    />
  );

  useEffect(() => {
    setBackButtonPortalTarget(document.getElementById('login-body'));
  }, []);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleSubmit = () => {
    if (emailField === '') {
      setErrors([
        {
          path: 'email',
          messages: ["can't be blank"],
        },
      ]);
      return;
    }

    commitMutation({
      variables: {
        input: {
          login: {
            email: emailField,
          },
        },
      },
      onCompleted: (response) => {
        const responseData = response as ForgotPasswordLoginMutation$data;
        console.log('completed! response: ', responseData);
        if (!responseData || !responseData.loginForgotPassword) {
          setErrors([
            {
              path: 'base',
              messages: [
                'There was an error. Please contact Property Matrix support for assistance.',
              ],
            },
          ]);
          return;
        }
        if (
          responseData.loginForgotPassword.errors &&
          responseData.loginForgotPassword.errors.length > 0
        ) {
          setErrors(responseData.loginForgotPassword.errors as ErrorType[]);
          return;
        }
        setIsEmailSent(true);
      },
      onError: (error) => {
        setErrors([
          {
            path: 'base',
            messages: [
              'There was an error. Please contact Property Matrix support for assistance.',
            ],
          },
        ]);
        console.error(error);
      },
    });
  };

  if (isMutationInFlight)
    return <SubmitSpinner spinnerText={'Sending email...'} />;

  if (isEmailSent)
    return (
      <SuccessPage
        successText="Email sent"
        successSubText="Please check your email for a link to reset your password."
        variant="login-success"
      />
    );

  return (
    <div className={loginStyles.loginRouteContent}>
      {backButtonPortalTarget &&
        createPortal(
          <button
            className={loginStyles.loginBackButton}
            onClick={() => navigate('../email')}
            type="button"
          >
            <BackIcon />
          </button>,
          backButtonPortalTarget
        )}
      {somethingWentWrongAlert}
      <PageHeader title="Reset password" includeBottomMargin={false} />
      <form className={loginStyles.loginForm} onSubmit={handleFormSubmit}>
        <Text
          field={{
            label: 'Email',
            name: 'email',
            type: 'email',
            value: emailField,
            autocomplete: 'username',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={(fieldName, value) =>
            setEmailField(value?.toString() || '')
          }
          errors={filterErrorsByPath(errors, ['email'])}
        />
        <button type="submit" hidden /> {/* Allows submit on enter */}
      </form>
      <PortalButton
        onClick={handleSubmit}
        extraClasses={[loginStyles.loginPrimaryButton]}
      >
        Reset password
      </PortalButton>
    </div>
  );
};

export default ForgotPassword;
